import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { Button, Modal } from 'react-daisyui';
import { Trans, useTranslation } from 'react-i18next';
import { formatPrice } from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.functions';
import { ePlanCode } from 'src/providers/SubscriptionProvider/Subscription.enums';
import { useUserContext } from 'src/providers/UserProvider/User.context';
import { tPlanNotSuitableConfirmationModalProps } from '../ConfirmationModal.types';

export default function PlanNotSuitableConfirmationModal(
  props: tPlanNotSuitableConfirmationModalProps,
) {
  const {
    isOpen,
    basicPlanPrice,
    basicPlanCurrency,
    nextInvoiceDate,
    invoiceAmount,
    formatDate,
    onClose,
  } = props;
  const { t } = useTranslation('settings/SubscriptionManager');
  const { currentUser } = useUserContext();

  const planName = t(`page.Plans.${ePlanCode.PLAN_BASIC}`);

  return (
    <Modal open={isOpen} className="max-w-[631px] rounded-lg p-0">
      <Modal.Header className="flex flex-row justify-end p-4 pb-0 mb-0">
        <Button
          color="ghost"
          size="sm"
          shape="circle"
          onClick={onClose}
          aria-label={t('cancelSubscription.offer.close')}
        >
          <svg
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="size-6"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </Button>
      </Modal.Header>
      <Modal.Body className="pt-0 px-6 pb-6 font-sans">
        <div className="flex flex-col gap-3 items-center">
          {/* Success Icon */}
          <img
            src="/images/subscriptions/retention-success.svg"
            alt="Plan Change Success"
            className="w-[199px] h-auto"
          />

          {/* Heading */}
          <div className="flex flex-col text-center">
            <h4 className="text-[28px] font-bold text-success">
              <Trans
                i18nKey="settings/SubscriptionManager:cancelSubscription.confirmationPlan.heading"
                values={{ name: currentUser?.name.split(' ')[0] }}
              />
            </h4>
            <p className="text-[28px] font-bold text-success">
              {t('cancelSubscription.confirmationPlan.subheading')}
            </p>
          </div>

          {/* Activated Features Section */}
          <div className="w-full">
            <p className="text-lg font-bold mb-3">
              {t('cancelSubscription.confirmationPlan.activatedTitle')}
            </p>
            <div className="flex flex-col gap-1">
              <div className="flex items-start gap-2">
                <CheckCircleIcon className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                <p className="text-base">
                  <Trans
                    i18nKey="settings/SubscriptionManager:cancelSubscription.confirmationPlan.benefit1"
                    components={{ bold: <strong /> }}
                  />
                </p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircleIcon className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                <p className="text-base">
                  <Trans
                    i18nKey="settings/SubscriptionManager:cancelSubscription.confirmationPlan.benefit2"
                    components={{ bold: <strong /> }}
                  />
                </p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircleIcon className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                <p className="text-base">
                  <Trans
                    i18nKey="settings/SubscriptionManager:cancelSubscription.confirmationPlan.benefit3"
                    components={{ bold: <strong /> }}
                  />
                </p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircleIcon className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                <p className="text-base">
                  <Trans
                    i18nKey="settings/SubscriptionManager:cancelSubscription.confirmationPlan.benefit4"
                    components={{ bold: <strong /> }}
                  />
                </p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircleIcon className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                <p className="text-base">
                  <Trans
                    i18nKey="settings/SubscriptionManager:cancelSubscription.confirmationPlan.benefit5"
                    components={{ bold: <strong /> }}
                  />
                </p>
              </div>
            </div>
          </div>

          {/* Plan and Billing Summary */}
          <div className="w-full bg-success/5 rounded py-6 px-8">
            <div className="flex flex-col gap-4">
              {/* Plan Name */}
              <div>
                <p className="text-lg">
                  {t('cancelSubscription.confirmationPlan.plan')}:{' '}
                  <span className="font-bold">{planName}</span>
                </p>
              </div>

              {/* Price — left out when the Basic plan is not in the loaded catalog */}
              {basicPlanPrice !== null && (
                <div className="flex flex-row justify-between items-center">
                  <p className="text-lg w-3/5">
                    {t('cancelSubscription.confirmationPlan.price')}
                  </p>
                  <p className="text-xl font-bold text-success flex-1">
                    $
                    {formatPrice({
                      price: basicPlanPrice,
                      currency: basicPlanCurrency,
                      includeDollarSign: false,
                    })}
                    /{t('page.SubscriptionPlans.perMonthly')}
                  </p>
                </div>
              )}

              {/* Separator */}
              <div className="border-t border-success" />

              {/* Next Invoice */}
              <div className="flex flex-col">
                <div className="flex flex-row justify-between items-center">
                  <p className="text-lg w-3/5">
                    {t('cancelSubscription.confirmationPlan.nextInvoice')}
                  </p>
                  <p className="flex-1">{formatDate(nextInvoiceDate)}</p>
                </div>

                {invoiceAmount !== null && (
                  <div className="flex flex-row justify-between items-center">
                    <p className="text-lg w-3/5">
                      {t('cancelSubscription.confirmationPlan.amount')}
                    </p>
                    <p className="font-bold text-success flex-1">
                      $
                      {formatPrice({
                        price: invoiceAmount,
                        currency: basicPlanCurrency,
                        includeDollarSign: false,
                      })}
                    </p>
                  </div>
                )}
              </div>

              {/* Separator */}
              <div className="border-t border-success" />

              {/* Footer Message */}
              <p className="text-xs text-center mt-4">
                {t('cancelSubscription.confirmationPlan.footerMessage')}
              </p>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}
